import { useCallback, useEffect, useRef, useState } from "react";
import { Play, Pause, Square, Clock } from "lucide-react";
import api, { whenVisible } from "@/api/client";
import { toast } from "sonner";

// Batch all timer lookups made within the same tick into one request.
let queue = new Map();
let flushT = null;
const flush = async () => {
  const q = queue; queue = new Map(); flushT = null;
  try {
    const { data } = await api.post("/task-timers/batch", { ids: [...q.keys()] });
    q.forEach((cbs, id) => cbs.forEach((c) => (data[id] ? c.res(data[id]) : c.rej(new Error("no access")))));
  } catch (e) { q.forEach((cbs) => cbs.forEach((c) => c.rej(e))); }
};
const loadTimer = (id) => new Promise((res, rej) => {
  if (!queue.has(id)) queue.set(id, []);
  queue.get(id).push({ res, rej });
  if (!flushT) flushT = setTimeout(flush, 40);
});

const fmtHMS = (secs) => {
  const s = Math.max(0, Math.round(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
};

// Human-friendly rollup for managers: "4.33 h" etc. Uses decimal hours only
// (no minutes) per Amit's Mar-01 2026 request — timer chip on live sessions
// still ticks in HH:MM:SS, but every rollup total is in hours.
export const fmtDayHours = (secs) => {
  const s = Math.max(0, Math.round(secs || 0));
  const hours = s / 3600;
  let tag = "";
  if (s >= 8 * 3600) tag = " · full day";
  else if (s >= 4 * 3600) tag = " · ½ day";
  return `${hours.toFixed(2)} h${tag}`;
};

/**
 * Small counter widget shown on a TaskCard. Assignee can start / pause / finish.
 * Managers see total hours logged for the task, aggregated across assignees.
 */
export default function TaskTimer({ task, currentUser, isManager, onStatusChange }) {
  const isAssignee = task.assigned_to === currentUser?.id;
  const canControl = isAssignee && task.status !== "complete";
  const [state, setState] = useState({ total: 0, running: null, breakdown: [] });
  const [, setTick] = useState(0); // forces re-render each second while running
  const pollRef = useRef(null);
  // Wall-clock time at the moment `state.total` was last synced from the server.
  // Combined with `state.total` this lets the UI tick smoothly between polls
  // without ever double-counting the currently-running session (the server's
  // `total_seconds` already includes the live delta for the running session,
  // so we only need to add wall-clock drift SINCE that reading — not the full
  // (now - started_at) span, which caused the 7h → 4h ghost bug).
  const lastLoadAtRef = useRef(Date.now());

  const load = useCallback(async () => {
    try {
      const data = await loadTimer(task.id);
      setState({ total: data.total_seconds || 0, running: data.running, breakdown: data.breakdown || [] });
      lastLoadAtRef.current = Date.now();
    } catch (_e) { /* silent — 403 for non-viewers is expected */ }
  }, [task.id]);

  useEffect(() => { load(); }, [load]);

  // Local tick while running, so counter appears live between polls
  useEffect(() => {
    if (!state.running) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [state.running]);

  // Reconcile drift with the server every 30s, only while this timer runs and the tab is visible
  useEffect(() => {
    if (!state.running && !isManager) return;
    pollRef.current = setInterval(whenVisible(load), 30000);
    return () => clearInterval(pollRef.current);
  }, [load, state.running, isManager]);

  // Server-truth total already covers the live running session UP TO the last
  // poll. We add only the wall-clock drift since that poll for smooth ticks.
  const displaySeconds = state.running
    ? state.total + Math.max(0, Math.floor((Date.now() - lastLoadAtRef.current) / 1000))
    : state.total;

  const start = async () => {
    try {
      await api.post(`/tasks/${task.id}/timer/start`);
      toast.success("Timer started");
      if (task.status === "not_started" && onStatusChange) {
        onStatusChange(task, "in_progress", {});
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start timer");
    }
  };

  const pause = async () => {
    try {
      await api.post(`/tasks/${task.id}/timer/pause`);
      toast.success("Timer paused");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not pause timer");
    }
  };

  const finish = async () => {
    if (!window.confirm("Mark this task as complete and stop the timer?")) return;
    try {
      await api.post(`/tasks/${task.id}/timer/stop`);
      toast.success("Task marked complete");
      await load();
      if (onStatusChange) onStatusChange(task, "complete", {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not finish");
    }
  };

  // Manual add-time flow removed — users now edit their TOTAL hours via the
  // "Edit total hours" row in the Task Dialog (POST /tasks/{id}/time/set),
  // which replaces the previous additive `+` prompt.

  const showBreakdown = isManager && state.breakdown.length > 0;

  return (
    <div
      data-testid={`task-timer-${task.id}`}
      className={`mt-2 flex flex-col gap-2 text-xs rounded-md border px-3 py-2 ${
        state.running ? "border-emerald-500/40 bg-emerald-500/5" : "border-white/8 bg-black/25"
      }`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-white/70 min-w-0 flex-1">
          <Clock size={12} className={`shrink-0 ${state.running ? "text-emerald-300 animate-pulse" : "text-white/40"}`} />
          <span className={`font-mono truncate ${state.running ? "text-emerald-300" : "text-white/80"}`}>
            {fmtHMS(displaySeconds)}
          </span>
          {state.running && <span className="text-[0.65rem] uppercase tracking-widest text-emerald-300/80 hidden sm:inline">Running</span>}
        </div>
        {canControl && (
          <div className="flex items-center gap-1 shrink-0">
            {!state.running ? (
              <button
                type="button"
                onClick={start}
                title="Start"
                data-testid={`task-timer-start-${task.id}`}
                className="h-6 w-6 rounded-full flex items-center justify-center bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-300 transition"
              >
                <Play size={11} />
              </button>
            ) : (
              <button
                type="button"
                onClick={pause}
                title="Pause"
                data-testid={`task-timer-pause-${task.id}`}
                className="h-6 w-6 rounded-full flex items-center justify-center bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-300 transition"
              >
                <Pause size={11} />
              </button>
            )}
            <button
              type="button"
              onClick={finish}
              title="Finish (mark complete)"
              data-testid={`task-timer-finish-${task.id}`}
              className="h-6 w-6 rounded-full flex items-center justify-center bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-300 transition"
            >
              <Square size={11} />
            </button>
          </div>
        )}
      </div>
      {showBreakdown && (
        <div className="text-[0.7rem] text-white/40 leading-relaxed border-t border-white/5 pt-1.5">
          {state.breakdown.map((b) => (
            <div key={b.user_id} className="flex justify-between">
              <span>{b.name || b.user_id.slice(-6)}</span>
              <span className="font-mono">{fmtDayHours(b.seconds)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
